import React from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';

import {
  useActiveSettlementMethodQuery,
  useSetDesiredActiveSettlementMethodMutation,
} from 'apollo/queries/autogenerate/hooks';
import { DesiredSettlementMethod } from 'apollo/queries/autogenerate/schemas';
import SevenEleven from 'shared/assets/images/settlements/711/711@3x.png';
import LogoFamima from 'shared/assets/images/settlements/familymart/logo@3x.png';
import LogoLawson from 'shared/assets/images/settlements/loppi/logo-lawson@3x.png';
import LogoMinistop from 'shared/assets/images/settlements/loppi/logo-ministop@3x.png';
import LogoSeicomart from 'shared/assets/images/settlements/seicomart/logo@3x.png';
import Badge from 'shared/modules/common/components/atoms/Badge';
import Button from 'shared/modules/common/components/atoms/Button';
import {
  ACTION,
  ITEM,
  SCREEN,
} from 'shared/modules/settlement/constants/mixpanel';
import { getFormattedSettlementDate } from 'shared/utils/date';
import Mixpanel from 'shared/vendor/Mixpanel';

//TODO: Add 711 logo
const logos = [
  LogoLawson,
  LogoMinistop,
  LogoFamima,
  LogoSeicomart,
  SevenEleven,
];

type Props = {
  isAutoDebitActive: boolean;
  isBillGenerated: boolean;
  nextAutoDebitByDate: string;
  settlementPeriodEnd?: string;
  billingCycle: string;
};

export const CombiniSection = ({
  isAutoDebitActive,
  isBillGenerated,
  nextAutoDebitByDate,
  settlementPeriodEnd,
  billingCycle,
}: Props) => {
  const [
    setDesiredActiveSettlementMethod,
  ] = useSetDesiredActiveSettlementMethodMutation();

  const {
    refetch: refetchActiveSettlementMethod,
  } = useActiveSettlementMethodQuery({
    variables: {
      billingCycle,
    },
    fetchPolicy: 'cache-and-network',
    skip: billingCycle === '',
  });

  async function handleUpdateSettlementMethod() {
    await setDesiredActiveSettlementMethod({
      variables: {
        billingCycle,
        desiredSettlementMethod: DesiredSettlementMethod.COMBINI_OR_VAN,
      },
    });

    refetchActiveSettlementMethod();
  }

  return (
    <View style={styles.section}>
      <View style={styles.titleContainer}>
        <Text style={[styles.sectionHeading, { marginRight: 14 }]}>
          コンビニ・銀行振込で支払う
        </Text>
        {!isAutoDebitActive && <Badge title="ご利用中" variant="success" />}
      </View>
      <Text style={styles.subHeading}>お支払い期間</Text>
      <Text style={[styles.text, styles.textLight]}>
        {getFormattedSettlementDate(settlementPeriodEnd)}
      </Text>
      <Text style={[styles.sectionHeading, { marginBottom: 14 }]}>
        コンビニ
      </Text>
      <Text style={styles.subHeading}>手数料</Text>
      <Text style={[styles.text, styles.textLight]}>最大390円（税込）</Text>
      <Text style={styles.subHeading}>取り扱いコンビニ</Text>
      <View style={styles.logos}>
        {logos.map((logo, index) => (
          <Image source={{ uri: logo }} key={index} style={styles.icon} />
        ))}
      </View>

      <Text style={[styles.sectionHeading, { marginBottom: 14 }]}>
        銀行振込
      </Text>

      <Text style={styles.subHeading}>お支払い期間</Text>
      <Text style={[styles.text, styles.textLight]}>
        振込手数料がかかる場合があります。詳しくはご利用の銀行にてご確認ください。
      </Text>
      <Text style={styles.subHeading}>支払い方法</Text>
      <Text style={[styles.text, styles.textLight]}>
        {`お支払い期間中に、ペイディの銀行口座にご請求金額をお支払いください。${
          isBillGenerated && isAutoDebitActive
            ? `\n\n口座振替の手続き中のため、${nextAutoDebitByDate}より変更できます。`
            : ''
        }`}
      </Text>
      {isAutoDebitActive && (
        <Button
          variant="primary"
          title="選択する"
          disabled={isBillGenerated}
          style={styles.button}
          onPress={() => {
            Mixpanel.trackEvent(`${SCREEN.SETTLEMENT_INFO} - ${ACTION.CLICK}`, {
              Item: ITEM.CHANGE_TO_COMBINI,
            });

            handleUpdateSettlementMethod();
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  titleContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: 16,
  },
  button: {
    marginHorizontal: 'auto',
  },
  section: {
    backgroundColor: 'white',
    padding: 24,
    borderRadius: 24,
    marginBottom: 24,
  },
  sectionHeading: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 'bold',
    color: '#2A2E32',
  },
  text: {
    fontSize: 13,
    lineHeight: 16,
    color: '#2A2E32',
    marginBottom: 16,
  },
  textLight: {
    color: '#586065',
  },
  subHeading: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '600',
    color: '#586065',
    marginBottom: 8,
  },
  logos: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  icon: {
    height: 40,
    width: 40,
    marginRight: 8,
  },
});
